"use client";
import Image from "next/image";
import { RichtextRenderer } from "@/components/atoms/rich-text-render";import { useLocale } from "next-intl";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import { useCallback, useMemo } from "react";
import {
  Japan,
  SharedHeroBannerDatasource,
  JapanArea,
} from "@/types/generated";
import RegionSelection from "./RegionSelection";
import useSearchFormStore from "@/store/searchFormStore/searchFormStore";
import { SearchAvailableRoom } from "../search-avaliable/SearchAvailableRoomMobile";
import { useRouter } from "next/navigation";

export interface RegionOption {
  value: string;
  label: string;
  destination: string;
  slug?: string;
  searchNames: string[];
}

export interface BookingUrlParams {
  destination: string;
  rooms: string;
  datein: string;
  dateout: string;
  currency: string;
}

export interface DestinationBannerProps {
  destinationData: Japan;
  bannerDatasource?: SharedHeroBannerDatasource;
}

/**
 * DestinationBanner Component
 * Displays a dynamic destination banner with an image, localized text, and a selectable region dropdown.
 * Also handles navigation when a region is selected.
 */
export const DestinationBanner = ({
  destinationData,
  bannerDatasource,
}: DestinationBannerProps) => {
  const { japanAreas } = useJapanAreasStore();
  const router = useRouter();
  const { setFormData, formData } = useSearchFormStore();
  const locale = useLocale();

  // Memoize region options to avoid recalculation on every render
  const regionOptions: RegionOption[] = useMemo(() => {
    return (
      (japanAreas || [])
        .map((area: JapanArea) => {
          const title = area?.title || "";
          const itemName = area?.item_name || "";
          const areaName = area?.area_name || "";

          // Combine multiple possible names for better search flexibility
          const generatedSearchNames = Array.from(
            new Set(
              [title, itemName, areaName]
                .filter(Boolean)
                .map((name) => name.toString()),
            ),
          );

          return {
            value: area?.documentId || "",
            label: title,
            destination: itemName || areaName,
            slug: area?.slug_attraction || "",
            searchNames: generatedSearchNames,
          };
        })
        // Exclude invalid or incomplete options
        .filter((option) => option.value && option.label) as RegionOption[]
    );
  }, [japanAreas]);

  // Handle user selecting a region from the dropdown
  // Updates global form state and redirects to region-specific page
  const handleRegionSelect = useCallback(
    (region: RegionOption) => {
      if (region?.slug) {
        setFormData({
          ...(formData as SearchAvailableRoom),
          hotelOrCity: {
            label: region.label,
            value: region.value,
            type: "prefecture",
            city: region.destination,
            slug: region.slug,
          },
        });
        router.push(`/${locale}/${region?.slug}`);
      }
    },
    [locale, formData, setFormData, router],
  );

  const { cover_image, title, description } = destinationData || {};
  const bannerImage = cover_image?.cloudflare_url || cover_image?.url;

  return (
    <div className="relative">
      <div className="h-[188px] w-full overflow-hidden md:h-[450px] xl:h-[720px]">
        <Image
          src={bannerImage || ""}
          alt={cover_image?.alternativeText || ""}
          aria-label={cover_image?.caption || ""}
          fill
          className="object-cover"
          priority
        />
      </div>

      {bannerDatasource && (
        <div className="pointer-events-none absolute -bottom-35 z-2 w-full md:top-1/2 md:bottom-auto md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:px-5">
          <div className="mx-auto w-11/12 rounded-xl border border-gray-300 bg-white px-4 py-5 shadow-xl md:border-none md:bg-transparent md:p-6 md:shadow-none">
            <div className="mb-2 text-center text-lg text-[var(--text-base-primary)] sm:text-2xl md:mb-3 md:text-[36px] md:text-white xl:text-[52px]">
              <RichtextRenderer
                customStyle={{
                  "": {
                    fontWeight: 600,
                    color: "inherit",
                    fontSize: "inherit",
                  },
                }}
                content={title || ""}
              />
            </div>
            {description && (
              <div className="text-center text-sm leading-relaxed text-[var(--text-gray-stronger)] md:text-xl md:text-[24px] md:text-white xl:text-[36px]">
                <RichtextRenderer
                  customStyle={{
                    "": {
                      color: "inherit",
                    },
                  }}
                  content={description || ""}
                />
              </div>
            )}
            <RegionSelection
              regionOptions={regionOptions}
              onRegionSelect={handleRegionSelect}
              bannerDatasource={bannerDatasource}
              destinationTitle={title || ""}
            />
          </div>
        </div>
      )}
    </div>
  );
};
